import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, parseErr } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Logo } from '@/components/brand/Logo';
import { Lock, LogOut } from 'lucide-react';
import { toast } from 'sonner';

/**
 * ChangePasswordPage — used in two contexts:
 *
 *  1. FORCED (post-admin-reset): the Protected wrapper in App.js routes
 *     users here when `user.force_password_change` is true. They cannot
 *     access any other page until they set a new password.
 *  2. VOLUNTARY: reachable from Settings for a routine password rotation.
 *
 * On success we refresh /auth/me so `force_password_change` clears and the
 * user is dropped onto Home.
 */
export default function ChangePasswordPage() {
  const { user, logout, refreshMe } = useAuth();
  const nav = useNavigate();
  const [current, setCurrent] = useState('');
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const forced = !!user?.force_password_change;

  useEffect(() => {
    if (!user) nav('/login', { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (pw1.length < 8) return setError('New password must be at least 8 characters.');
    if (pw1 !== pw2) return setError('Passwords do not match.');
    if (pw1 === current) return setError('New password must differ from the current one.');
    setBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: pw1 });
      toast.success('Password updated');
      await refreshMe();
      nav('/home', { replace: true });
    } catch (e) {
      setError(parseErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-10 border-r"
           style={{ background: 'hsl(var(--surface-1))', borderColor: 'hsl(var(--border))' }}>
        <Logo variant="horizontal" surface="light" height={44} className="max-w-[280px]" />
        <div>
          <h1 className="font-heading text-4xl font-semibold leading-tight tracking-tight">
            {forced ? 'Set a new password.' : 'Change your password.'}
          </h1>
          <p className="mt-4 text-[hsl(var(--foreground-muted))] max-w-md text-[15px] leading-relaxed">
            {forced
              ? 'Your account was reset by an admin. Choose a permanent password to continue — the temp password you just used will stop working.'
              : 'Pick something strong. Minimum 8 characters — a mix of letters, numbers, and symbols is a good habit.'}
          </p>
        </div>
        <div className="text-[12px] text-[hsl(var(--foreground-subtle))]">
          Signed in as <span className="font-mono">{user?.email}</span>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-10">
        <form onSubmit={submit} className="w-full max-w-sm space-y-4" data-testid="change-password-form">
          <div className="lg:hidden flex justify-center mb-2">
            <Logo variant="stacked" surface="light" height={90} />
          </div>
          <div className="space-y-1">
            <h2 className="font-heading text-2xl font-semibold flex items-center gap-2">
              <Lock size={22} />{forced ? 'Set new password' : 'Change password'}
            </h2>
            {forced && (
              <p className="text-sm text-[hsl(var(--warning-foreground,#8a5b00))] p-2 rounded bg-[hsl(48_96%_94%)] border"
                 style={{ borderColor: 'hsl(48 90% 80%)' }}
                 data-testid="change-password-forced-note">
                Your account was reset by an admin. You must choose a new password before continuing.
              </p>
            )}
          </div>

          <label className="block">
            <span className="text-[12px] font-medium text-[hsl(var(--foreground-muted))]">
              {forced ? 'Temp password (from admin)' : 'Current password'}
            </span>
            <Input data-testid="change-password-current"
                   required type="password" value={current}
                   onChange={e => setCurrent(e.target.value)}
                   className="mt-1" autoComplete="current-password" />
          </label>
          <label className="block">
            <span className="text-[12px] font-medium text-[hsl(var(--foreground-muted))]">New password</span>
            <Input data-testid="change-password-new"
                   required type="password" value={pw1}
                   onChange={e => setPw1(e.target.value)}
                   minLength={8} maxLength={128}
                   placeholder="At least 8 characters"
                   className="mt-1" autoComplete="new-password" />
          </label>
          <label className="block">
            <span className="text-[12px] font-medium text-[hsl(var(--foreground-muted))]">Confirm new password</span>
            <Input data-testid="change-password-confirm"
                   required type="password" value={pw2}
                   onChange={e => setPw2(e.target.value)}
                   minLength={8} maxLength={128}
                   className="mt-1" autoComplete="new-password" />
          </label>

          {error && (
            <div className="text-[12.5px] text-[hsl(var(--danger))] p-2 rounded border"
                 style={{ borderColor: 'hsl(var(--danger))', background: 'hsl(0 90% 97%)' }}
                 role="alert" data-testid="change-password-error">
              {error}
            </div>
          )}

          <Button data-testid="change-password-submit" disabled={busy} type="submit" className="w-full">
            {busy ? 'Saving…' : 'Save password'}
          </Button>

          {forced && (
            <button type="button"
                    onClick={() => { logout(); nav('/login', { replace: true }); }}
                    className="w-full text-[12.5px] text-[hsl(var(--foreground-muted))] underline inline-flex items-center justify-center gap-1"
                    data-testid="change-password-signout">
              <LogOut size={13} />Sign out instead
            </button>
          )}
        </form>
      </div>
    </div>
  );
}
